import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, hasPerm, initials, tagColorClass, TAG_DOT, TAG_COLOR_KEYS, tagColorKey } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import StudentRowActions from "@/pages/students/StudentRowActions";
import ExportDialog from "@/components/ExportDialog";
import AssignTransportDialog from "@/pages/AssignTransportDialog";
import { FeeCardButton } from "@/components/FeeCardDialog";
import {
  Search, UserPlus, Users2, Loader2, SlidersHorizontal, Trash2, Upload, Download,
  Plus, X, FileSpreadsheet, CheckSquare, Columns3, ArrowUp, ArrowDown, Eye, EyeOff, Tags, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

export const empty = {
  admission_number: "", name: "", mother_name: "", father_name: "", dob: "", gender: "Male",
  blood_group: "", height: "", weight: "", class_id: "", section: "", stream: "", roll_number: "", admission_date: "",  academic_session: "2026-27", phone: "", alt_phone: "", address: "", city: "", state: "",
  pin_code: "", aadhar_number: "", pen_number: "", apaar_id: "", photo: "", admission_class: "", admission_section: "", caste: "",
  religion: "", category: "General", house: "", nationality: "Indian", tag: "", tags: [], promise_date: "", promise_amount: "",
  whatsapp: "", on_udise: "No", rte_student: "No", admission_type: "New",
  father_qualification: "", mother_qualification: "",
  father_income: "", mother_income: "", tc_status: "Not Applicable", previous_school: "", status: "Active",
};

function YesNo({ label, value, onChange, name }) {
  return (
    <div>
      <Label>{label}</Label>
      <RadioGroup value={value} onValueChange={onChange} className="flex gap-6 mt-2" data-testid={`student-${name}`}>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
          <RadioGroupItem value="Yes" data-testid={`student-${name}-yes`} /> Yes
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
          <RadioGroupItem value="No" data-testid={`student-${name}-no`} /> No
        </label>
      </RadioGroup>
    </div>
  );
}

const PARENT_ROLES = [
  { key: "mother", label: "Mother" },
  { key: "father", label: "Father" },
  { key: "guardian", label: "Guardian" },
];
const PARENT_ROWS = [
  { field: "name", label: "Name", type: "text" },
  { field: "qualification", label: "Qualification", type: "select" },
  { field: "res_address", label: "Residential Address", type: "text" },
  { field: "occupation", label: "Occupation", type: "text" },
  { field: "off_address", label: "Official Address", type: "text" },
  { field: "income", label: "Annual Income", type: "text" },
  { field: "email", label: "Email Address", type: "text" },
  { field: "mobile", label: "Mobile No.", type: "text" },
  { field: "aadhar", label: "Aadhar No.", type: "text" },
  { field: "photo", label: "Photo", type: "file" },
];
const QUAL_OPTIONS = ["Below 10th", "10th Pass", "12th Pass", "Diploma", "Graduate", "Post Graduate", "Doctorate", "Other"];

function ParentsTable({ f, set }) {
  const onFile = (key) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Image too large (max 1.5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => set(key, reader.result);
    reader.readAsDataURL(file);
  };
  const cell = (row, key) => {
    if (row.type === "select") {
      return (
        <Select value={f[key] || ""} onValueChange={(v) => set(key, v)}>
          <SelectTrigger className="h-9" data-testid={`student-${key}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{QUAL_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (row.type === "file") {
      return (
        <div className="flex items-center gap-2">
          {f[key] ? <img src={f[key]} alt="" className="w-10 h-10 rounded object-cover border border-slate-200" /> : <span className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center text-[9px] text-slate-300 shrink-0">No Photo</span>}
          <Input type="file" accept="image/*" onChange={onFile(key)} className="h-9 text-xs" data-testid={`student-${key}`} />
        </div>
      );
    }
    return <Input className="h-9" value={f[key] || ""} onChange={(e) => set(key, e.target.value)} data-testid={`student-${key}`} />;
  };
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-lg">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-emerald-50/70 text-slate-700">
            <th className="text-left px-3 py-2.5 border border-slate-200 w-52 font-bold">Details</th>
            {PARENT_ROLES.map((r) => <th key={r.key} className="text-left px-3 py-2.5 border border-slate-200 font-bold">{r.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {PARENT_ROWS.map((row) => (
            <tr key={row.field}>
              <td className="px-3 py-2 border border-slate-200 font-medium text-slate-700 align-middle">{row.label}</td>
              {PARENT_ROLES.map((r) => (
                <td key={r.key} className="px-2 py-1.5 border border-slate-200 align-middle">{cell(row, `${r.key}_${row.field}`)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SectionTitle({ children }) {  return (
    <div className="flex items-center gap-3 mt-2 mb-1">
      <h3 className="font-heading font-bold text-slate-800 text-sm uppercase tracking-wider whitespace-nowrap">{children}</h3>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

// Natural school-grade ordering (Nursery, LKG, UKG, 1st, 2nd …). Class names come from DB; this only ranks them.
const PRE_PRIMARY_ORDER = ["pg", "play group", "playgroup", "pre-nursery", "pre nursery", "prenursery", "nursery", "nur", "lkg", "ukg", "kg", "prep", "kindergarten"];
const classSortKey = (name) => {
  const s = (name || "").trim().toLowerCase();
  const pre = PRE_PRIMARY_ORDER.indexOf(s);
  if (pre !== -1) return [0, pre, s];
  const num = parseInt((s.match(/^\d+/) || [])[0], 10);
  if (!isNaN(num)) return [1, num, s];
  return [2, 0, s];
};
export const sortClassNames = (names) => [...names].sort((a, b) => {
  const ka = classSortKey(a), kb = classSortKey(b);
  if (ka[0] !== kb[0]) return ka[0] - kb[0];
  if (ka[1] !== kb[1]) return ka[1] - kb[1];
  return ka[2].localeCompare(kb[2]);
});

export function StudentFields({ f, set, setF, classes, tags = [], colors = {}, onAddTag, siblingsSlot = null }) {
  const [streams, setStreams] = useState([]);
  const [fieldOpts, setFieldOpts] = useState({});
  useEffect(() => {
    api.get("/streams").then((r) => setStreams(r.data.streams || [])).catch(() => {});
    api.get("/student-field-settings").then((r) => setFieldOpts(r.data.fields || {})).catch(() => {});
  }, []);
  const classRecords = classes || [];
  const classNames = sortClassNames(Array.from(new Set(classRecords.map((c) => c.name).filter(Boolean))));
  const admissionClassOpts = sortClassNames(Array.from(new Set([...classNames, ...(f.admission_class ? [f.admission_class] : [])])));
  const admissionSectionOpts = f.admission_class
    ? Array.from(new Set([...classRecords.filter((c) => c.name === f.admission_class && c.section).map((c) => c.section), ...(f.admission_section ? [f.admission_section] : [])]))
    : [];
  const selectedClass = classRecords.find((c) => c.id === f.class_id);
  const selectedClassName = selectedClass ? selectedClass.name : "";
  const classSections = Array.from(new Set(
    classRecords.filter((c) => c.name === selectedClassName && c.section).map((c) => c.section)
  ));
  const sectionOpts = selectedClassName
    ? Array.from(new Set([...classSections, ...(f.section ? [f.section] : [])]))
    : [];
  const streamOpts = Array.from(new Set([...streams, ...(f.stream ? [f.stream] : [])]));
  const pickClassName = (name) => {
    const recs = classRecords.filter((c) => c.name === name);
    const chosen = recs.find((c) => c.section) || recs[0];
    setF((p) => ({ ...p, class_id: chosen ? chosen.id : "", section: chosen ? (chosen.section || "") : "" }));
  };
  const pickSection = (sec) => {
    const rec = classRecords.find((c) => c.name === selectedClassName && c.section === sec);
    setF((p) => ({ ...p, section: sec, class_id: rec ? rec.id : p.class_id }));
  };
  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Image too large (max 1.5MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => set("photo", reader.result);
    reader.readAsDataURL(file);
  };
  const fRef = useRef(f); fRef.current = f;
  const setRef = useRef(set); setRef.current = set;
  const F = useCallback(({ k, label, type = "text" }) => (
    <div><Label>{label}</Label><Input type={type} value={(fRef.current || {})[k] || ""} onChange={(e) => setRef.current(k, e.target.value)} data-testid={`student-${k}`} /></div>
  ), []);
  const fieldOptions = (key, current) => {
    const base = fieldOpts[key] || [];
    return Array.from(new Set([...base, ...(current ? [current] : [])]));
  };
  const SettingSelect = ({ k, label }) => {
    const opts = fieldOptions(k, f[k]);
    return (
      <div><Label>{label}</Label>
        <Select value={f[k] || ""} onValueChange={(v) => set(k, v)}><SelectTrigger data-testid={`student-${k}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{opts.length === 0 ? <div className="px-2 py-1.5 text-xs text-slate-400">Add options in Settings → Student</div> : opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
      </div>
    );
  };
  return (
    <>
        <SectionTitle>Student Information</SectionTitle>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center text-slate-300 shrink-0">
            {f.photo ? <img src={f.photo} alt="student" className="w-full h-full object-cover" /> : <span className="text-xs">No Photo</span>}
          </div>
          <div>
            <Label>Student Photo</Label>
            <Input type="file" accept="image/*" onChange={onPhoto} data-testid="student-photo" className="mt-1" />
            <p className="text-xs text-slate-400 mt-1">JPG/PNG up to 1.5MB</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <F k="admission_number" label="Admission Number *" />
          <F k="name" label="Student Name *" />
          <F k="dob" label="Date of Birth" type="date" />
          <SettingSelect k="gender" label="Gender" />
          <SettingSelect k="blood_group" label="Blood Group" />
          <F k="height" label="Height (cm)" type="number" />
          <F k="weight" label="Weight (kg)" type="number" />
          <div><Label>Class *</Label>
            <Select value={selectedClassName} onValueChange={pickClassName}><SelectTrigger data-testid="student-class"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>{classNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Section</Label>
            <Select value={f.section || ""} onValueChange={pickSection} disabled={!selectedClassName}><SelectTrigger data-testid="student-section"><SelectValue placeholder={selectedClassName ? "Select section" : "Select class first"} /></SelectTrigger>
              <SelectContent>{sectionOpts.length === 0 ? <div className="px-2 py-1.5 text-xs text-slate-400">No sections for this class</div> : sectionOpts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Stream</Label>
            <Select value={f.stream || "none"} onValueChange={(v) => set("stream", v === "none" ? "" : v)}><SelectTrigger data-testid="student-stream"><SelectValue placeholder="Select stream" /></SelectTrigger>
              <SelectContent><SelectItem value="none">— None —</SelectItem>{streamOpts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </div>
          <F k="roll_number" label="Roll Number" />
          <F k="admission_date" label="Admission Date" type="date" />
          <div><Label>Admission In Class</Label>
            <Select value={f.admission_class || ""} onValueChange={(v) => setF((p) => ({ ...p, admission_class: v, admission_section: "" }))}><SelectTrigger data-testid="student-admission_class"><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>{admissionClassOpts.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Admission Section</Label>
            <Select value={f.admission_section || ""} onValueChange={(v) => set("admission_section", v)} disabled={!f.admission_class}><SelectTrigger data-testid="student-admission_section"><SelectValue placeholder={f.admission_class ? "Select section" : "Select class first"} /></SelectTrigger>
              <SelectContent>{admissionSectionOpts.length === 0 ? <div className="px-2 py-1.5 text-xs text-slate-400">No sections for this class</div> : admissionSectionOpts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </div>
          <SettingSelect k="admission_type" label="Admission Type" />
          <F k="academic_session" label="Session" />
          <F k="phone" label="Phone" />
          <F k="whatsapp" label="WhatsApp" />
          <F k="alt_phone" label="Alternate Phone" />
          <F k="aadhar_number" label="Aadhar Number" />
          <F k="pen_number" label="PEN Number" />
          <F k="apaar_id" label="Apaar ID" />
          <SettingSelect k="caste" label="Caste" />
          <SettingSelect k="religion" label="Religion" />
          <SettingSelect k="category" label="Category" />
          <SettingSelect k="nationality" label="Nationality" />
          <SettingSelect k="house" label="House" />
          <F k="previous_school" label="Previous School" />
          <div><Label>Tag</Label>
            <div className="mt-1 min-h-[38px] rounded-md border border-slate-200 px-2 py-1.5" data-testid="student-tag">
              <TagPicker value={f.tags || []} allTags={tags} colors={colors} onAddNew={onAddTag} onChange={(v) => set("tags", v)} testid="form" />
            </div>
          </div>
          <F k="promise_date" label="Fee Promise Date" type="date" />
          <F k="promise_amount" label="Fee Promise Amount" type="number" />
          <SettingSelect k="tc_status" label="TC Status" />
          <YesNo label="Is Student on UDISE?" name="on_udise" value={f.on_udise || "No"} onChange={(v) => set("on_udise", v)} />
          <YesNo label="Is RTE Student?" name="rte_student" value={f.rte_student || "No"} onChange={(v) => set("rte_student", v)} />
        </div>

        <SectionTitle>Parents / Guardian Information</SectionTitle>
        <ParentsTable f={f} set={set} />

        {siblingsSlot}

        <SectionTitle>Address Information</SectionTitle>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-3"><F k="address" label="Address" /></div>
          <F k="city" label="City" />
          <F k="state" label="State" />
          <F k="pin_code" label="PIN Code" />
        </div>
    </>
  );
}

export function StudentForm({ open, onOpenChange, classes, edit, initial, onSaved, onAdded, addTitle, tags = [], colors = {}, onAddTag }) {
  const [f, setF] = useState(empty);
  useEffect(() => {
    if (edit) {
      const t = (edit.tags && edit.tags.length) ? edit.tags : (edit.tag ? [edit.tag] : []);
      setF({ ...empty, ...edit, tags: t });
    } else setF(initial ? { ...empty, ...initial } : empty);
  }, [edit, initial, open]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...f, tags: f.tags || [], tag: (f.tags && f.tags[0]) || "", promise_amount: Number(f.promise_amount) || 0 };
      if (edit) await api.put(`/students/${edit.id}`, payload);
      else {
        const res = await api.post("/students", payload);
        if (onAdded) await onAdded(res.data);
      }
      toast.success(edit ? "Student updated" : "Student added");
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[70vw] max-w-[70vw] max-h-[85vh] overflow-y-auto" data-testid="student-form-dialog">
        <DialogHeader><DialogTitle>{edit ? "Edit Student" : (addTitle || "Add Student")}</DialogTitle></DialogHeader>
        <StudentFields f={f} set={set} setF={setF} classes={classes} tags={tags} colors={colors} onAddTag={onAddTag} />
        <DialogFooter><Button onClick={save} data-testid="save-student-btn">{edit ? "Update" : "Add Student"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CATEGORIES = ["General", "OBC", "SC", "ST", "Other"];
const GENDERS = ["Male", "Female", "Other"];
const PAGE_SIZES = [20, 50, 100, 200, 500];

const FILTER_FIELDS = [
  { key: "class_name", label: "Class", type: "class" },
  { key: "section", label: "Section", type: "section" },
  { key: "gender", label: "Gender", type: "select", options: GENDERS },
  { key: "category", label: "Category", type: "select", options: CATEGORIES },
  { key: "house", label: "House", type: "text" },
  { key: "on_udise", label: "On UDISE", type: "select", options: ["Yes", "No"] },
  { key: "admission_type", label: "Admission Type", type: "select", options: ["New", "Old", "Left"] },
  { key: "rte_student", label: "RTE Student", type: "select", options: ["Yes", "No"] },
  { key: "tag", label: "Tag", type: "select", options: ["Topper", "Good", "Average", "Defaulter", "New"] },
  { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
  { key: "min_due", label: "Due Fees (min ₹)", type: "number" },
  { key: "max_due", label: "Due Fees (max ₹)", type: "number" },
  { key: "transport", label: "Transport User", type: "bool" },
  { key: "fee_due", label: "Has Fee Due", type: "bool" },
];

const SORT_OPTIONS = [
  { value: "name", label: "Name" },
  { value: "admission_number", label: "Admission No" },
  { value: "roll_number", label: "Roll No" },
  { value: "section", label: "Section" },
  { value: "father_name", label: "Father Name" },
  { value: "created_at", label: "Date Added" },
];

function DueBadge({ due, total }) {
  if (due <= 0) return <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">No Due</span>;
  const high = total > 0 && due / total > 0.5;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${high ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{high ? "High Due" : "Partial"}</span>;
}

const TAG_PALETTE_KEYS = TAG_COLOR_KEYS;

function TagChip({ label, onRemove, colors }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-md px-1.5 py-0.5 border ${tagColorClass(label, colors)}`}>
      {label}
      {onRemove && <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="hover:opacity-60"><X size={11} /></button>}
    </span>
  );
}

function TagPicker({ value = [], allTags = [], onAddNew, onChange, testid = "", colors = {} }) {
  const [openP, setOpenP] = useState(false);
  const toggle = (t) => onChange(value.includes(t) ? value.filter((x) => x !== t) : [...value, t]);
  const createNew = async () => {
    const n = onAddNew && await onAddNew();
    if (n && !value.includes(n)) onChange([...value, n]);
  };
  const opts = Array.from(new Set([...(allTags || []), ...value]));
  return (
    <div className="flex flex-wrap items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {value.map((t) => <TagChip key={t} label={t} colors={colors} onRemove={() => toggle(t)} />)}
      {value.length === 0 && <span className="text-xs text-slate-300">No tags</span>}
      <Popover open={openP} onOpenChange={setOpenP}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`add-tag-${testid}`}
            className="w-5 h-5 rounded-full border border-dashed border-indigo-300 text-indigo-500 hover:bg-indigo-50 flex items-center justify-center shrink-0">
            <Plus size={12} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-52 p-1" data-testid="tag-picker-panel">
          <div className="text-[11px] font-semibold text-slate-400 uppercase px-2 py-1">Select Tags</div>
          <div className="max-h-52 overflow-auto">
            {opts.map((t) => (
              <button key={t} type="button" onClick={() => toggle(t)} data-testid={`tag-opt-${t}`}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-sm text-left">
                <Checkbox checked={value.includes(t)} className="pointer-events-none" />
                <span className={`w-2.5 h-2.5 rounded-full ${TAG_DOT[tagColorKey(t, colors)]}`} />
                <span className="flex-1 text-slate-700">{t}</span>
              </button>
            ))}
            {opts.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No tags yet</div>}
          </div>
          <div className="border-t border-slate-100 mt-1 pt-1">
            <button type="button" onClick={createNew} data-testid="create-new-tag"
              className="w-full flex items-center gap-1.5 px-2 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded">
              <Plus size={14} /> Create New Tag
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ManageTagsDialog({ open, onOpenChange, tags, colors, onColorChange, onCreate }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="manage-tags-dialog">
        <DialogHeader><DialogTitle>Tag Colours</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {tags.length === 0 && <p className="text-sm text-slate-400">No tags yet. Create one below.</p>}
          {tags.map((t) => (
            <div key={t} className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2">
              <TagChip label={t} colors={colors} />
              <div className="flex-1 flex flex-wrap gap-1.5 justify-end">
                {TAG_PALETTE_KEYS.map((ck) => (
                  <button key={ck} type="button" title={ck} data-testid={`tag-color-${t}-${ck}`}
                    onClick={() => onColorChange(t, ck)}
                    className={`w-6 h-6 rounded-full ${TAG_DOT[ck]} transition-transform hover:scale-110 ${tagColorKey(t, colors) === ck ? "ring-2 ring-offset-1 ring-slate-800" : ""}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCreate} data-testid="manage-create-tag"><Plus size={15} className="mr-1" />New Tag</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteStudentDialog({ open, onOpenChange, student, onConfirm }) {
  const [reason, setReason] = useState("");
  const [leaveStatus, setLeaveStatus] = useState("Left");
  const [leaveDate, setLeaveDate] = useState("");
  useEffect(() => { if (open) { setReason(""); setLeaveStatus("Left"); setLeaveDate(new Date().toISOString().slice(0, 10)); } }, [open]);
  if (!student) return null;
  const f = student.fees_summary || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="delete-student-dialog">
        <DialogHeader><DialogTitle className="text-rose-600">Remove Student?</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">You are about to remove <b>{student.name}</b> ({student.admission_number}). Review their fee details before confirming.</p>
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
            {[["Total Payable", f.total_payable], ["Paid", f.paid], ["Discount", f.discount]].map(([k, v]) => (
              <div key={k} className="flex justify-between px-3 py-1.5"><span className="text-slate-500">{k}</span><span className="font-medium tabular">{fmt(v || 0)}</span></div>
            ))}
            <div className="flex justify-between px-3 py-1.5 bg-rose-50">
              <span className="text-rose-600 font-semibold">Due Fees</span>
              <span className={`font-bold tabular ${(f.due || 0) > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(f.due || 0)}</span>
            </div>
          </div>
          {(f.due || 0) > 0 && <p className="text-xs text-rose-500">⚠ This student still has pending dues of {fmt(f.due)}.</p>}
          <div>
            <Label>Reason for removal</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Transferred, left school…" data-testid="delete-reason-input" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Deleting Status</Label>
              <Select value={leaveStatus} onValueChange={setLeaveStatus}>
                <SelectTrigger className="mt-1" data-testid="delete-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Left">Left</SelectItem>
                  <SelectItem value="Pass out">Pass out</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{leaveStatus} Date</Label>
              <Input type="date" value={leaveDate} onChange={(e) => setLeaveDate(e.target.value)} data-testid="delete-date-input" className="mt-1" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="delete-cancel-btn">Cancel</Button>
          <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => onConfirm({ reason, leave_status: leaveStatus, leave_date: leaveDate })} data-testid="delete-confirm-btn">Remove Student</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TagCell({ s, tags, colors, onChange, onAddNew }) {
  const cur = (s.tags && s.tags.length) ? s.tags : (s.tag ? [s.tag] : []);
  return (
    <div className="max-w-[200px]" data-testid={`tag-${s.admission_number}`}>
      <TagPicker value={cur} allTags={tags} colors={colors} onAddNew={onAddNew} onChange={onChange} testid={s.admission_number} />
    </div>
  );
}

function PromiseCell({ s, patchMeta }) {
  const [date, setDate] = useState(s.promise_date || "");
  const [amount, setAmount] = useState(s.promise_amount ?? "");
  const save = () => {
    const changed = (date || "") !== (s.promise_date || "") || Number(amount || 0) !== Number(s.promise_amount || 0);
    if (changed) patchMeta(s.id, { promise_date: date || "", promise_amount: Number(amount || 0) });
  };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = s.fees_summary?.due || 0;
  const overdue = date && new Date(date) < today && due > 0;
  return (
    <div className="flex flex-col gap-1 w-32" onClick={(e) => e.stopPropagation()} data-testid={`promise-cell-${s.admission_number}`}>
      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} onBlur={save} className={`h-7 text-xs px-1.5 ${overdue ? "border-rose-400 text-rose-600" : ""}`} data-testid={`promise-date-${s.admission_number}`} />
      <Input type="number" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} onBlur={save} className="h-7 text-xs px-1.5 tabular" data-testid={`promise-amount-${s.admission_number}`} />
      {overdue && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px] font-semibold" data-testid={`promise-overdue-${s.admission_number}`}>
          <AlertTriangle size={11} />Promise Overdue
        </span>
      )}
    </div>
  );
}

const COL_DEFS = {
  serial: { label: "Se. No.", align: "left", render: (s, ctx) => <span className="text-slate-500 tabular">{(ctx.page - 1) * ctx.limit + ctx.idx + 1}</span> },
  roll: { label: "Roll No.", align: "left", render: (s) => <span className="text-slate-500">{s.roll_number || "—"}</span> },
  student: {
    label: "Student", align: "left", render: (s, ctx) => (
      <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => ctx.nav(`/students/${s.id}`)}>
        <div className="w-20 h-20 rounded-lg bg-indigo-100 text-indigo-700 overflow-hidden flex items-center justify-center text-lg font-semibold shrink-0 border border-slate-200">
          {s.photo ? <img src={s.photo} alt={s.name} className="w-full h-full object-cover" /> : initials(s.name)}
        </div>
        <div><div className="font-medium text-slate-800">{s.name}</div>{s.house && <div className="text-xs text-slate-400">{s.house} House</div>}</div>
      </div>
    ),
  },
  adm_no: { label: "Adm No", align: "left", render: (s) => <span className="text-slate-500">{s.admission_number}</span> },
  class: { label: "Class", align: "left", render: (s) => `${s.class_name} ${s.section}` },
  father: { label: "Father", align: "left", render: (s) => <span className="text-slate-500">{s.father_name}</span> },
  phone: {
    label: "Phone", align: "left", render: (s) => (
      <div className="text-xs leading-tight space-y-0.5 whitespace-nowrap">
        <div className="text-slate-700">{s.phone || "—"}</div>
        {s.whatsapp && <div className="text-emerald-600">WA: {s.whatsapp}</div>}
        {s.alt_phone && <div className="text-slate-400">Alt: {s.alt_phone}</div>}
      </div>
    ),
  },
  tag: { label: "Tag", align: "left", render: (s, ctx) => <TagCell s={s} tags={ctx.tags} colors={ctx.colors} onChange={(v) => ctx.patchMeta(s.id, { tags: v })} onAddNew={ctx.addTagName} /> },
  status: {
    label: "Status", align: "left", render: (s, ctx) => {
      const on = s.status === "Active";
      return (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Switch checked={on} onCheckedChange={() => ctx.patchMeta(s.id, { status: on ? "Inactive" : "Active" })}
            className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-300" data-testid={`status-${s.admission_number}`} />
          <span className={`text-xs font-semibold ${on ? "text-emerald-600" : "text-slate-400"}`}>{on ? "" : ""}</span>
        </div>
      );
    },
  },
  promise: {
    label: "Fee Promise", align: "left", render: (s, ctx) => <PromiseCell s={s} patchMeta={ctx.patchMeta} />,
  },
  fees: {
    label: "Fees", align: "left", render: (s) => {
      const f = s.fees_summary;
      return (
        <div className="text-[11px] leading-tight whitespace-nowrap space-y-0.5">
          <div className="text-slate-600">Total: {fmt(f.total_payable)}</div>
          <div className="text-slate-600">Transport: {fmt(f.transport_fee)}</div>
          <div>Paid: <span className="text-emerald-600">{fmt(f.paid)}</span></div>
          <div>Discount: <span className="text-amber-600">{fmt(f.discount)}</span></div>
          <div>Due: <span className={`font-semibold ${f.due > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(f.due)}</span></div>
        </div>
      );
    },
  },
  total: { label: "Total", align: "right", render: (s) => <span className="tabular text-slate-600">{fmt(s.fees_summary.total_payable)}</span> },
  transport: { label: "Transport", align: "right", render: (s, ctx) => (
    <div className="flex flex-col items-end gap-0.5">
      <span className="tabular text-slate-500">{fmt(s.fees_summary.transport_fee)}</span>
      <button onClick={(e) => { e.stopPropagation(); ctx.onAssignTransport?.(s); }} className="text-[11px] text-indigo-600 hover:underline font-medium" data-testid={`assign-transport-link-${s.admission_number}`}>Add/Edit Transport</button>
    </div>
  ) },
  paid: { label: "Paid", align: "right", render: (s) => <span className="tabular text-emerald-600">{fmt(s.fees_summary.paid)}</span> },
  discount: { label: "Disc.", align: "right", render: (s) => <span className="tabular text-amber-600">{fmt(s.fees_summary.discount)}</span> },
  due: { label: "Due", align: "right", render: (s) => (<div className="flex flex-col items-end gap-0.5"><span className={`font-bold tabular ${s.fees_summary.due > 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(s.fees_summary.due)}</span><DueBadge due={s.fees_summary.due} total={s.fees_summary.total_payable} /></div>) },
  docs: {
    label: "Docs", align: "left", render: (s) => {
      const d = s.docs || {};
      const items = [["Photo", d.photo], ["Aadhar", d.aadhar], ["DOB", d.dob], ["TC", d.tc]];
      return (
        <div className="flex gap-1">
          {items.map(([label, ok]) => (
            <span key={label} title={`${label}: ${ok ? "uploaded" : "missing"}`} className={`w-6 h-5 rounded text-[9px] font-bold flex items-center justify-center ${ok ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-300"}`}>{label[0]}</span>
          ))}
        </div>
      );
    },
  },
  fee_card: {
    label: "Fee Card", align: "left", render: (s) => (
      <div onClick={(e) => e.stopPropagation()} title="Open fee card (print / save as PDF)">
        <FeeCardButton studentId={s.id} label="" size="sm" variant="outline" className="px-2" testId={`fee-card-btn-${s.admission_number}`} />
      </div>
    ),
  },
};
const DEFAULT_COL_ORDER = ["serial", "roll", "student", "fee_card", "adm_no", "class", "father", "phone", "tag", "status", "docs", "fees", "total", "transport", "paid", "discount", "due", "promise"];

function loadStored(key, fallback) {
  try { const v = JSON.parse(localStorage.getItem(key)); return v || fallback; } catch { return fallback; }
}

function ImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setMapping({}); setResult(null); };

  const downloadTemplate = async () => {
    const r = await api.get("/students/import/template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "student-import-template.xlsx"; a.click(); URL.revokeObjectURL(url);
  };

  const runPreview = async (theFile, theMapping) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", theFile);
      if (theMapping && Object.keys(theMapping).length) fd.append("mapping", JSON.stringify(theMapping));
      const r = await api.post("/students/import/preview", fd);
      setPreview(r.data);
      if (!theMapping || !Object.keys(theMapping).length) {
        const init = {};
        r.data.fields.forEach((f) => { if (r.data.headers.includes(f)) init[f] = f; });
        setMapping(init);
      }
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onFile = (f) => { setFile(f); setResult(null); setPreview(null); setMapping({}); if (f) runPreview(f, {}); };
  const changeMap = (field, header) => {
    const m = { ...mapping };
    if (header === "__none__") delete m[field]; else m[field] = header;
    setMapping(m);
    if (file) runPreview(file, m);
  };

  const confirmImport = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      if (Object.keys(mapping).length) fd.append("mapping", JSON.stringify(mapping));
      const r = await api.post("/students/import", fd);
      setResult(r.data);
      toast.success(`Imported ${r.data.created} students`);
      onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const statusChip = { new: "bg-emerald-100 text-emerald-700", duplicate: "bg-amber-100 text-amber-700", error: "bg-rose-100 text-rose-700" };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Import Students from Excel</DialogTitle></DialogHeader>

        {!preview && !result && (
          <div className="space-y-4">
            <button onClick={downloadTemplate} className="flex items-center gap-3 w-full p-3 rounded-lg border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40" data-testid="download-template-btn">
              <FileSpreadsheet size={24} className="text-emerald-600" />
              <div className="text-left"><div className="font-medium text-slate-800">Download Sample Template</div><div className="text-xs text-slate-400">Fill class_name matching your classes</div></div>
            </button>
            <div>
              <Label>Upload File (.xlsx)</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={(e) => onFile(e.target.files?.[0])} data-testid="import-file-input" />
              <p className="text-xs text-slate-400 mt-1">You'll get a preview before anything is saved. Duplicates are skipped.</p>
            </div>
            {busy && <div className="flex justify-center py-4"><Loader2 className="animate-spin text-indigo-500" /></div>}
          </div>
        )}

        {preview && !result && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 text-sm" data-testid="preview-summary">
              <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium">{preview.summary.new} new</span>
              <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">{preview.summary.duplicate} duplicates (skip)</span>
              <span className="px-3 py-1 rounded-full bg-rose-100 text-rose-700 font-medium">{preview.summary.error} errors</span>
            </div>

            <details className="border border-slate-200 rounded-lg p-3" open={preview.summary.total === 0}>
              <summary className="cursor-pointer text-sm font-medium text-slate-700">Field Mapping ({preview.headers.length} columns detected)</summary>
              {preview.summary.total === 0 && (
                <p className="text-xs text-amber-600 mt-2" data-testid="mapping-hint">No rows matched automatically — assign your Excel columns to the fields below (at least Admission Number, Name and Class).</p>
              )}
              <div className="grid sm:grid-cols-2 gap-2 mt-3 max-h-52 overflow-y-auto">
                {preview.fields.map((f) => (
                  <div key={f} className="flex items-center gap-2">
                    <span className="text-xs w-32 shrink-0 text-slate-500">{f}{["admission_number", "name", "class_name"].includes(f) && <span className="text-rose-500">*</span>}</span>
                    <Select value={mapping[f] || "__none__"} onValueChange={(v) => changeMap(f, v)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`map-${f}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— none —</SelectItem>
                        {preview.headers.filter(Boolean).map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </details>

            <div className="border border-slate-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0"><tr><th className="px-2 py-2 text-left">Row</th><th className="px-2 py-2 text-left">Status</th><th className="px-2 py-2 text-left">Name</th><th className="px-2 py-2 text-left">Adm No</th><th className="px-2 py-2 text-left">Class</th><th className="px-2 py-2 text-left">Section</th><th className="px-2 py-2 text-left">Issues</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.rows.map((r) => (
                    <tr key={r.row} className={r.status === "error" ? "bg-rose-50/40" : ""}>
                      <td className="px-2 py-1.5">{r.row}</td>
                      <td className="px-2 py-1.5"><span className={`px-2 py-0.5 rounded-full ${statusChip[r.status]}`}>{r.status}</span></td>
                      <td className="px-2 py-1.5">{r.data.name}</td>
                      <td className="px-2 py-1.5">{r.data.admission_number}</td>
                      <td className="px-2 py-1.5">{r.data.class_name}</td>
                      <td className="px-2 py-1.5" data-testid={`preview-section-${r.data.admission_number}`}>{r.data.section || <span className="text-slate-300">—</span>}</td>
                      <td className="px-2 py-1.5 text-rose-600">{r.errors.join("; ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={reset}>Choose Another File</Button>
              <Button onClick={confirmImport} disabled={busy || preview.summary.new === 0} data-testid="confirm-import-btn">
                {busy ? <Loader2 className="animate-spin" /> : `Import ${preview.summary.new} Students`}
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 p-4 text-sm space-y-1" data-testid="import-result">
              <div className="text-emerald-600 font-medium">✓ {result.created} students created</div>
              <div className="text-amber-600">↷ {result.skipped} skipped (duplicates)</div>
              {result.errors?.length > 0 && <div className="text-rose-600"><div className="font-medium">{result.errors.length} error(s):</div><ul className="list-disc pl-5 max-h-32 overflow-y-auto">{result.errors.map((e, i) => <li key={i} className="text-xs">{e}</li>)}</ul></div>}
            </div>
            <div className="flex justify-end"><Button onClick={() => { reset(); onOpenChange(false); }} data-testid="import-done-btn">Done</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Students() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ students: [], total: 0 });
  const [search, setSearch] = useState("");
  const [classes, setClasses] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [applied, setApplied] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [tags, setTags] = useState(["Topper", "Good", "Average", "Defaulter", "New"]);
  const [tagColors, setTagColors] = useState({});
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [colOrder, setColOrder] = useState(() => {
    const stored = loadStored("students_col_order_v3", DEFAULT_COL_ORDER);
    const merged = [...stored.filter((k) => COL_DEFS[k]), ...DEFAULT_COL_ORDER.filter((k) => !stored.includes(k))];
    return merged;
  });
  const [hiddenCols, setHiddenCols] = useState(() => loadStored("students_col_hidden_v3", []));

  useEffect(() => { localStorage.setItem("students_col_order_v3", JSON.stringify(colOrder)); }, [colOrder]);
  useEffect(() => { localStorage.setItem("students_col_hidden_v3", JSON.stringify(hiddenCols)); }, [hiddenCols]);

  const visibleCols = colOrder.filter((k) => COL_DEFS[k] && !hiddenCols.includes(k)).map((k) => ({ key: k, ...COL_DEFS[k] }));
  const moveCol = (key, dir) => {
    const i = colOrder.indexOf(key), j = i + dir;
    if (j < 0 || j >= colOrder.length) return;
    const arr = [...colOrder]; [arr[i], arr[j]] = [arr[j], arr[i]]; setColOrder(arr);
  };
  const toggleCol = (key) => setHiddenCols(hiddenCols.includes(key) ? hiddenCols.filter((k) => k !== key) : [...hiddenCols, key]);

  const patchMeta = async (id, body) => {
    try { await api.patch(`/students/${id}/meta`, body); toast.success("Updated"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const addTagName = async () => {
    const name = window.prompt("New tag name:");
    if (!name || !name.trim()) return null;
    try { const r = await api.post("/settings/tags", { tag: name.trim() }); setTags(r.data.tags); setTagColors(r.data.colors || {}); return name.trim(); }
    catch (e) { toast.error("Could not add tag"); return null; }
  };
  const onAddTag = async (id) => { const name = await addTagName(); if (name) await patchMeta(id, { tags: [name] }); };
  const setTagColor = async (tag, color) => {
    setTagColors((p) => ({ ...p, [tag]: color }));
    try { const r = await api.post("/settings/tags/color", { tag, color }); setTagColors(r.data.colors || {}); }
    catch (e) { toast.error("Could not set colour"); }
  };
  const confirmDelete = async (payload) => {
    if (!delTarget) return;
    try {
      await api.delete(`/students/${delTarget.id}`, { data: payload });
      toast.success("Student removed"); setDelTarget(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); api.get("/settings/tags").then((r) => { setTags(r.data.tags || []); setTagColors(r.data.colors || {}); }).catch(() => {}); }, []);

  const reqSeq = useRef(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const seq = ++reqSeq.current;
    const params = { page, limit };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    Object.entries(applied).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
    api.get("/students", { params })
      .then((r) => { if (seq === reqSeq.current) { setData(r.data); setSelected([]); } })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  }, [debouncedSearch, applied, page, limit]);

  useEffect(() => { load(); }, [load]);

  const [transportStudent, setTransportStudent] = useState(null);

  const setFilter = (key, value) => {
    setApplied((prev) => {
      const next = { ...prev };
      if (value === "" || value == null || value === "__any__") {
        delete next[key];
        if (key === "class_name") delete next.section;
      } else {
        next[key] = value;
      }
      return next;
    });
    setPage(1);
  };
  const clearFilters = () => { setApplied({}); setPage(1); };

  const allOnPage = data.students.map((s) => s.id);
  const sectionsForClassName = (name) =>
    Array.from(new Set(classes.filter((c) => !name || c.name === name).map((c) => c.section).filter(Boolean)));
  const toggleAll = () => setSelected(selected.length === allOnPage.length ? [] : allOnPage);
  const toggleOne = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const bulkDelete = async () => {
    if (!selected.length) { toast.error("No students selected"); return; }
    if (!window.confirm(`Remove ${selected.length} selected students? They can be restored from Deleted Students.`)) return;
    const reason = window.prompt("Reason for removal (optional):") || "";
    await api.post("/students/bulk-delete", { ids: selected, scope: "selected", filters: { reason } });
    toast.success("Selected students removed"); load();
  };
  const doExport = async ({ mode, format, fields }) => {
    const body = { scope: "filtered", filters: {}, format, udise: mode === "udise", fields: mode === "selected" ? fields : [] };
    const r = await api.post("/students/export", body, { responseType: "blob" });
    const ext = format === "pdf" ? "pdf" : "xlsx";
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `students-export.${ext}`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const totalPages = Math.ceil(data.total / limit) || 1;
  const canAdd = hasPerm(user, "add_student");

  const RibbonBtn = ({ icon: Icon, label, onClick, testid, active, disabled }) => (
    <Button variant={active ? "default" : "outline"} size="sm" onClick={onClick} data-testid={testid} disabled={disabled} className="gap-1.5">
      <Icon size={15} />{label}
    </Button>
  );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900">Students</h1>
        <p className="text-slate-500">{data.total} students {Object.keys(applied).length > 0 && "(filtered)"}</p>
      </div>

      {/* Action ribbon */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {canAdd && <RibbonBtn icon={UserPlus} label="Add" onClick={() => nav("/students/new")} testid="add-student-btn" active />}
          {hasPerm(user, "delete") && <RibbonBtn icon={Trash2} label={`Delete${selected.length ? ` (${selected.length})` : ""}`} onClick={bulkDelete} testid="bulk-delete-btn" disabled={!selected.length} />}
          {canAdd && <RibbonBtn icon={Upload} label="Import" onClick={() => setImportOpen(true)} testid="import-btn" />}
          {hasPerm(user, "export") && <RibbonBtn icon={Download} label="Export" onClick={() => setExportOpen(true)} testid="export-btn" />}
          <RibbonBtn icon={CheckSquare} label={selected.length === allOnPage.length && allOnPage.length ? "Unselect All" : "Select All"} onClick={toggleAll} testid="select-all-btn" />
          <RibbonBtn icon={SlidersHorizontal} label="Advanced Filter" onClick={() => setShowFilter(!showFilter)} testid="advanced-filter-btn" active={showFilter || Object.keys(applied).length > 0} />
          {hasPerm(user, "edit_student") && <RibbonBtn icon={Tags} label="Tag Colours" onClick={() => setManageTagsOpen(true)} testid="manage-tags-btn" />}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="columns-btn"><Columns3 size={15} />Columns</Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-2" data-testid="columns-panel">
              <div className="text-xs font-semibold text-slate-500 uppercase px-2 py-1">Show / Reorder Columns</div>
              {colOrder.filter((k) => COL_DEFS[k]).map((k, idx) => (
                <div key={k} className="flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-50">
                  <button onClick={() => toggleCol(k)} className="text-slate-500" data-testid={`col-toggle-${k}`}>
                    {hiddenCols.includes(k) ? <EyeOff size={15} className="text-slate-300" /> : <Eye size={15} className="text-indigo-600" />}
                  </button>
                  <span className={`flex-1 text-sm ${hiddenCols.includes(k) ? "text-slate-400" : "text-slate-700"}`}>{COL_DEFS[k].label}</span>
                  <button onClick={() => moveCol(k, -1)} disabled={idx === 0} className="text-slate-400 disabled:opacity-30 hover:text-slate-700"><ArrowUp size={14} /></button>
                  <button onClick={() => moveCol(k, 1)} disabled={idx === colOrder.length - 1} className="text-slate-400 disabled:opacity-30 hover:text-slate-700"><ArrowDown size={14} /></button>
                </div>
              ))}
            </PopoverContent>
          </Popover>
          <div className="flex items-center gap-2" data-testid="page-size-wrap">
            <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-20 h-9" data-testid="page-size-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Search name, adm no, father, phone, class, address…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="student-search" />
          </div>
        </div>

        {showFilter && (
          <div className="border-t border-slate-100 pt-3" data-testid="filter-panel">
            <div className="flex flex-wrap items-end gap-2.5">
              <div className="flex flex-col gap-1" data-testid="filter-chip-sort_by">
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide px-0.5">Sort By</label>
                <Select value={applied.sort_by || "__any__"} onValueChange={(v) => setFilter("sort_by", v)}>
                  <SelectTrigger className="w-36 h-9" data-testid="filter-sort-by"><SelectValue placeholder="Default" /></SelectTrigger>
                  <SelectContent><SelectItem value="__any__">Default</SelectItem>{SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1" data-testid="filter-chip-sort_dir">
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide px-0.5">Order</label>
                <Select value={applied.sort_dir || "asc"} onValueChange={(v) => setFilter("sort_dir", v)}>
                  <SelectTrigger className="w-28 h-9" data-testid="filter-sort-dir"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="asc">A → Z</SelectItem><SelectItem value="desc">Z → A</SelectItem></SelectContent>
                </Select>
              </div>
              {FILTER_FIELDS.map((field) => {
                const val = applied[field.key] ?? "";
                const classNames = Array.from(new Set(classes.map((c) => c.name).filter(Boolean)));
                return (
                  <div key={field.key} className="flex flex-col gap-1" data-testid={`filter-chip-${field.key}`}>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide px-0.5">{field.label}</label>
                    {field.type === "class" ? (
                      <Select value={val || "__any__"} onValueChange={(v) => setFilter(field.key, v)}>
                        <SelectTrigger className="w-40 h-9" data-testid="filter-class-select"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent><SelectItem value="__any__">Any</SelectItem>{classNames.map((nm) => <SelectItem key={nm} value={nm}>{nm}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : field.type === "section" ? (
                      (() => {
                        const secOpts = sectionsForClassName(applied.class_name);
                        return (
                          <Select value={val || "__any__"} onValueChange={(v) => setFilter(field.key, v)}>
                            <SelectTrigger className="w-32 h-9" data-testid="filter-section-select"><SelectValue placeholder="Any" /></SelectTrigger>
                            <SelectContent><SelectItem value="__any__">Any</SelectItem>{secOpts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        );
                      })()
                    ) : field.type === "select" ? (
                      <Select value={val || "__any__"} onValueChange={(v) => setFilter(field.key, v)}>
                        <SelectTrigger className="w-36 h-9" data-testid={`filter-${field.key}-select`}><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent><SelectItem value="__any__">Any</SelectItem>{(field.key === "tag" ? tags : field.options).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : field.type === "bool" ? (
                      <Select value={val || "__any__"} onValueChange={(v) => setFilter(field.key, v)}>
                        <SelectTrigger className="w-28 h-9" data-testid={`filter-${field.key}-select`}><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent><SelectItem value="__any__">Any</SelectItem><SelectItem value="true">Yes</SelectItem></SelectContent>
                      </Select>
                    ) : (
                      <Input className="w-36 h-9" type={field.type === "number" ? "number" : "text"} value={val} onChange={(e) => setFilter(field.key, e.target.value)} placeholder="Any" data-testid={`filter-${field.key}-input`} />
                    )}
                  </div>
                );
              })}
              {Object.keys(applied).length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filter-btn" className="h-9 text-rose-500"><X size={14} className="mr-1" />Clear All</Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowFilter(false)} data-testid="hide-filter-btn" className="h-9"><X size={14} className="mr-1" />Hide Filters</Button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 table-scroll">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          data.students.length === 0 ? (
            <div className="py-16 text-center text-slate-400"><Users2 size={40} className="mx-auto mb-2 opacity-40" />No students found.</div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-300px)]">
              <table className="w-full text-sm min-w-[1100px] border border-slate-200 border-collapse">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0 z-20">
                  <tr>
                    <th className="px-2 py-2 w-10 bg-slate-50 border border-slate-200"><Checkbox checked={selected.length === allOnPage.length && allOnPage.length > 0} onCheckedChange={toggleAll} data-testid="header-select-all" /></th>
                    {visibleCols.map((c) => <th key={c.key} className={`px-2 py-2 bg-slate-50 border border-slate-200 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>)}
                    <th className="px-2 py-2 bg-slate-50 border border-slate-200">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.students.map((s, idx) => {
                    return (
                      <tr key={s.id} className="hover:bg-slate-50" data-testid={`student-row-${s.admission_number}`}>
                        <td className="px-2 py-2 border border-slate-200"><Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggleOne(s.id)} data-testid={`select-${s.admission_number}`} /></td>
                        {visibleCols.map((c) => <td key={c.key} className={`px-2 py-2 align-middle border border-slate-200 ${c.align === "right" ? "text-right" : ""}`}>{c.render(s, { idx, page, limit, patchMeta, nav, tags, colors: tagColors, addTagName, onAssignTransport: setTransportStudent })}</td>)}
                        <td className="px-2 py-2 border border-slate-200"><StudentRowActions student={s} user={user} onChanged={load} onEdit={(st) => nav(`/students/${st.id}/edit`)} onDelete={(st) => setDelTarget(st)} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {data.total > limit && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-500">Page {page} of {totalPages} · {data.total} total</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="prev-page">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page">Next</Button>
          </div>
        </div>
      )}

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} entityLabel="student" fieldsUrl="/students/export-fields" onExport={doExport} />
      <ManageTagsDialog open={manageTagsOpen} onOpenChange={setManageTagsOpen} tags={tags} colors={tagColors} onColorChange={setTagColor} onCreate={addTagName} />
      <DeleteStudentDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)} student={delTarget} onConfirm={confirmDelete} />
      <AssignTransportDialog open={!!transportStudent} onOpenChange={(o) => !o && setTransportStudent(null)} student={transportStudent} onAssigned={load} />
    </div>
  );
}
